import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useLang } from "../../i18n/LangContext";
import { api, extractError } from "../../api/client";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Plus, Eye, EyeOff } from "lucide-react";
import { L } from "./lib";
import { DayForm, blankDay } from "./cms/DayForm";
import { JourneyForm, blankJourney } from "./cms/JourneyForm";
import { CmsDayRow } from "./cms/CmsDayRow";

export function JourneyEditor() {
  const { t, lang } = useLang();
  const [journeys, setJourneys] = useState([]);
  const [active, setActive] = useState(null);
  const [days, setDays] = useState([]);
  const [editing, setEditing] = useState(null);
  const [newJourney, setNewJourney] = useState(null);

  const loadJourneys = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/education/journeys");
      setJourneys(data.data);
      setActive((prev) => (prev ? data.data.find((j) => j.journey_id === prev.journey_id) || prev : data.data[0] || null));
    } catch (e) { toast.error(extractError(e).message); }
  }, []);

  const loadDays = useCallback(async () => {
    if (!active) return;
    try {
      const { data } = await api.get(`/admin/education/journeys/${active.journey_id}/days`);
      setDays(data.data);
    } catch (e) { toast.error(extractError(e).message); }
  }, [active]);

  useEffect(() => { loadJourneys(); }, [loadJourneys]);
  useEffect(() => { loadDays(); }, [loadDays]);

  const run = async (fn, msg) => {
    try { await fn(); toast.success(msg); loadDays(); loadJourneys(); }
    catch (e) { toast.error(extractError(e).message); }
  };

  const saveDay = () =>
    run(async () => {
      const payload = {
        title: editing.title,
        description: editing.description,
        status: editing.status,
        content: (editing.content || []).map((c, i) => ({ ...c, order: i, url: c.url || null })),
      };
      if (editing.day_id) await api.patch(`/admin/education/days/${editing.day_id}`, payload);
      else await api.post(`/admin/education/journeys/${active.journey_id}/days`, payload);
      setEditing(null);
    }, t("common.saved"));

  const moveDay = (idx, dir) => {
    const next = [...days];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    run(() => api.post(`/admin/education/journeys/${active.journey_id}/days/reorder`, { day_ids: next.map((d) => d.day_id) }), t("edu.reordered"));
  };

  const createJourney = () =>
    run(async () => {
      await api.post("/admin/education/journeys", {
        name: newJourney.name,
        description: newJourney.description,
        journey_type: newJourney.journey_type,
        duration_days: Number(newJourney.duration_days),
        audience: newJourney.journey_type === "COACH_QUALIFICATION" ? "COACH" : "MEMBER",
        status: "DRAFT",
      });
      setNewJourney(null);
    }, t("edu.journeyCreated"));

  return (
    <div className="space-y-6" data-testid="journey-editor">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5 min-w-[240px]">
          <Label className="text-slate-300 text-sm">{t("edu.journey")}</Label>
          <Select value={active?.journey_id || ""} onValueChange={(v) => setActive(journeys.find((j) => j.journey_id === v))}>
            <SelectTrigger data-testid="cms-journey-select" className="bg-[#1C202E] border-white/10 text-white h-10"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#161923] border-white/10 text-slate-200">
              {journeys.map((j) => (
                <SelectItem key={j.journey_id} value={j.journey_id}>{L(j.name, lang)} · {j.duration_days} {t("edu.days")}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {active && (
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-sm">{t("edu.durationDays")}</Label>
            <div className="flex items-center gap-2">
              <Input
                data-testid="cms-duration-input"
                type="number"
                min={1}
                defaultValue={active.duration_days}
                key={active.journey_id}
                onBlur={(e) => {
                  const v = Number(e.target.value);
                  if (v && v !== active.duration_days) run(() => api.patch(`/admin/education/journeys/${active.journey_id}`, { duration_days: v }), t("common.saved"));
                }}
                className="w-24 bg-[#1C202E] border-white/10 text-white h-10"
              />
              <Button
                data-testid="cms-journey-publish-btn"
                variant="outline"
                onClick={() => run(() => api.patch(`/admin/education/journeys/${active.journey_id}`, { status: active.status === "PUBLISHED" ? "DRAFT" : "PUBLISHED" }), t("common.saved"))}
                className="h-10 border-white/15 bg-transparent text-slate-200 hover:bg-white/5 hover:text-white text-xs"
              >
                {active.status === "PUBLISHED" ? <EyeOff className="h-3.5 w-3.5 mr-1" /> : <Eye className="h-3.5 w-3.5 mr-1" />}
                {active.status === "PUBLISHED" ? t("education.unpublish") : t("education.publish")}
              </Button>
            </div>
          </div>
        )}

        <div className="flex gap-2 ml-auto">
          <Button data-testid="cms-new-journey-btn" variant="outline" onClick={() => setNewJourney({ ...blankJourney })}
            className="h-10 border-white/15 bg-transparent text-slate-200 hover:bg-white/5 hover:text-white">
            <Plus className="h-4 w-4 mr-1" /> {t("edu.newJourney")}
          </Button>
          {active && (
            <Button data-testid="cms-new-day-btn" onClick={() => setEditing({ ...blankDay, content: [] })}
              className="h-10 bg-[#FF3B30] hover:bg-[#E02E24] text-white font-semibold">
              <Plus className="h-4 w-4 mr-1" /> {t("edu.newDay")}
            </Button>
          )}
        </div>
      </div>

      {newJourney && (
        <JourneyForm journey={newJourney} onChange={setNewJourney} onSave={createJourney} onCancel={() => setNewJourney(null)} />
      )}

      {editing && (
        <DayForm day={editing} onChange={setEditing} onSave={saveDay} onCancel={() => setEditing(null)} />
      )}

      <div className="space-y-2">
        {days.map((d, i) => (
          <CmsDayRow
            key={d.day_id}
            day={d}
            onMove={(dir) => moveDay(i, dir)}
            onEdit={() => setEditing({ ...d, content: d.content || [] })}
            onDuplicate={() => run(() => api.post(`/admin/education/days/${d.day_id}/duplicate`), t("edu.duplicated"))}
            onTogglePublish={() => run(() => api.patch(`/admin/education/days/${d.day_id}`, { status: d.status === "PUBLISHED" ? "DRAFT" : "PUBLISHED" }), t("common.saved"))}
            onDelete={() => run(() => api.delete(`/admin/education/days/${d.day_id}`), t("common.deleted"))}
          />
        ))}
        {days.length === 0 && active && <p data-testid="cms-days-empty" className="text-sm text-slate-500">{t("edu.noDays")}</p>}
      </div>
    </div>
  );
}
